/**
 * Client-side vehicle search filters (Milestone 4).
 */
import { LISTING_PLACEHOLDER_IMAGE } from "../../constants/automapListingConstants";
import { RECENTLY_CONFIRMED_DAYS } from "../../constants/vehicleSearchParamsConstants";
import { resolveRiskLevel } from "../vehicleRiskDisplay";
import { RISK_LEVELS } from "../../constants/automapVehicleRiskConstants";
import { isMedellinValleyCity } from "../../constants/medellinValleyCoverageConstants.js";
import { dedupeNormalizedMakes, listingMatchesMakeFilter } from "./vehicleMakeNormalization.js";
import { parseVehicleSearchParams } from "./vehicleSearchParamsUrl";
import {
  canonicalListingBodyType,
  canonicalListingTransmission,
} from "../automapListing/normalizeVehicleListing";
import { listingFuelMatchesFuelFilter } from "./vehicleSearchFuelType";

function listingHaystack(listing) {
  return [
    listing.title,
    listing.make,
    listing.model,
    listing.trim,
    listing.version,
    listing.dealerName,
    listing.sellerName,
    listing.sourceName,
    listing.city,
    listing.department,
    listing.neighborhood,
    listing.description,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isRecentlyConfirmed(listing) {
  if (listing.availabilityStatus === "available" && listing.lastConfirmedAt) {
    const ts = new Date(listing.lastConfirmedAt).getTime();
    if (!Number.isNaN(ts)) {
      return Date.now() - ts <= RECENTLY_CONFIRMED_DAYS * 24 * 3_600_000;
    }
  }
  if (listing.availabilityStatus === "available") return true;
  return false;
}

function hasRealPhotos(listing) {
  const url = String(listing.primaryImageUrl || "").trim();
  if (!url) return false;
  if (url === LISTING_PLACEHOLDER_IMAGE) return false;
  if (url.includes("vehicle-placeholder")) return false;
  if (url.startsWith("/api/mobility/vehicle-stock-image.svg")) return false;
  if (listing.imageCount > 0) return true;
  return true;
}

function passesRange(value, min, max) {
  if (value == null) return min == null && max == null;
  if (min != null && value < min) return false;
  if (max != null && value > max) return false;
  return true;
}

/** Filters applied only on the client after API fetch. */
export const CLIENT_ONLY_FILTER_KEYS = new Set([
  "riskLevels",
  "hideHighRisk",
  "fairPriceLabels",
  "recentlyConfirmedOnly",
  "hasPhotos",
  "requirePhotos",
  "metroArea",
  "fuelTypes",
  "completeInfoOnly",
  "availabilityStatus",
]);

export function getClientOnlyFilters(params) {
  const p = parseVehicleSearchParams(params);
  return {
    riskLevels: p.riskLevels,
    hideHighRisk: p.hideHighRisk,
    fairPriceLabels: p.fairPriceLabels,
    recentlyConfirmedOnly: p.recentlyConfirmedOnly,
    hasPhotos: p.hasPhotos,
    requirePhotos: p.requirePhotos,
    metroArea: p.metroArea,
    fuelTypes: p.fuelTypes,
    completeInfoOnly: p.completeInfoOnly,
    availabilityStatus: p.availabilityStatus,
  };
}

export function hasClientOnlyFilters(params) {
  const p = parseVehicleSearchParams(params);
  return (
    p.hideHighRisk ||
    p.riskLevels?.length > 0 ||
    p.fairPriceLabels?.length > 0 ||
    p.recentlyConfirmedOnly ||
    p.hasPhotos ||
    p.requirePhotos ||
    p.metroArea ||
    (p.fuelTypes?.length > 0) ||
    p.completeInfoOnly ||
    Boolean(p.availabilityStatus)
  );
}

/**
 * @param {import('../../constants/vehicleSearchParamsConstants').VehicleSearchParams|object} params
 */
export function applyVehicleSearchFilters(listings, params) {
  const p = parseVehicleSearchParams(params);
  let next = listings;

  if (p.keyword) {
    const q = p.keyword.toLowerCase();
    next = next.filter((listing) => listingHaystack(listing).includes(q));
  }

  if (p.city) {
    const city = p.city.toLowerCase();
    next = next.filter((listing) => listing.city?.toLowerCase().includes(city));
  }

  if (p.department) {
    const dep = p.department.toLowerCase();
    next = next.filter((listing) => listing.department?.toLowerCase().includes(dep));
  }

  if (p.metroArea === "medellin_valley") {
    next = next.filter((listing) => isMedellinValleyCity(listing.city));
  }

  if (p.make) {
    next = next.filter((listing) => listingMatchesMakeFilter(listing.make, p.make));
  }

  if (p.model) {
    const model = p.model.toLowerCase();
    next = next.filter((listing) => listing.model?.toLowerCase() === model);
  }

  if (p.yearMin != null) {
    next = next.filter((listing) => listing.year == null || listing.year >= p.yearMin);
  }
  if (p.yearMax != null) {
    next = next.filter((listing) => listing.year == null || listing.year <= p.yearMax);
  }

  if (p.priceMinCop != null) {
    next = next.filter((listing) => listing.priceCop == null || listing.priceCop >= p.priceMinCop);
  }
  if (p.priceMaxCop != null) {
    next = next.filter((listing) => listing.priceCop == null || listing.priceCop <= p.priceMaxCop);
  }

  if (p.mileageMinKm != null) {
    next = next.filter((listing) => listing.mileageKm == null || listing.mileageKm >= p.mileageMinKm);
  }
  if (p.mileageMaxKm != null) {
    next = next.filter((listing) => listing.mileageKm == null || listing.mileageKm <= p.mileageMaxKm);
  }

  if (p.transmission) {
    next = next.filter(
      (listing) => canonicalListingTransmission(listing.transmission) === p.transmission
    );
  }

  const activeFuelTypes =
    p.fuelTypes?.length > 0 ? p.fuelTypes : p.fuelType ? [p.fuelType] : [];
  if (activeFuelTypes.length) {
    next = next.filter((listing) =>
      listingFuelMatchesFuelFilter(listing.fuelType, activeFuelTypes)
    );
  }

  if (p.bodyType) {
    next = next.filter(
      (listing) => canonicalListingBodyType(listing.bodyType) === p.bodyType
    );
  }

  if (p.dealerVerified) {
    next = next.filter((listing) => listing.dealerVerified === true);
  }

  if (p.fairPriceLabels?.length) {
    const allowed = new Set(p.fairPriceLabels);
    next = next.filter((listing) => {
      const label = listing.fairPriceLabel || "not_enough_data";
      if (allowed.has(label)) return true;
      if (allowed.has("not_enough_data") && label === "unknown") return true;
      return false;
    });
  }

  if (p.riskLevels?.length) {
    const allowed = new Set(p.riskLevels);
    next = next.filter((listing) => {
      const level = resolveRiskLevel(listing);
      if (allowed.has(level)) return true;
      if (allowed.has(RISK_LEVELS.UNKNOWN) && level === RISK_LEVELS.UNKNOWN) return true;
      return false;
    });
  }

  if (p.hideHighRisk) {
    next = next.filter(
      (listing) => ![RISK_LEVELS.MEDIUM, RISK_LEVELS.HIGH].includes(resolveRiskLevel(listing))
    );
  }

  if (p.recentlyConfirmedOnly) {
    next = next.filter((listing) => isRecentlyConfirmed(listing));
  }

  if (p.requirePhotos || p.hasPhotos) {
    next = next.filter((listing) => hasRealPhotos(listing));
  }

  if (p.availabilityStatus) {
    next = next.filter((listing) => listing.availabilityStatus === p.availabilityStatus);
  }

  if (p.completeInfoOnly) {
    next = next.filter(
      (listing) => !listing.hasIncompleteInfo && resolveRiskLevel(listing) !== RISK_LEVELS.UNKNOWN
    );
  }

  return next;
}

export function deriveAvailableFilters(listings) {
  const makes = new Set();
  const modelsByMake = new Map();
  const cities = new Set();
  const bodyTypes = new Set();

  listings.forEach((listing) => {
    if (listing.make) {
      makes.add(listing.make);
      if (listing.model) {
        const key = listing.make;
        if (!modelsByMake.has(key)) modelsByMake.set(key, new Set());
        modelsByMake.get(key).add(listing.model);
      }
    }
    if (listing.city) cities.add(listing.city);
    if (listing.bodyType && listing.bodyType !== "unknown") bodyTypes.add(listing.bodyType);
  });

  return {
    makes: dedupeNormalizedMakes([...makes]),
    modelsByMake: Object.fromEntries(
      [...modelsByMake.entries()].map(([make, models]) => [make, [...models].sort()])
    ),
    cities: [...cities].sort(),
    bodyTypes: [...bodyTypes].sort(),
  };
}
